import { useModeAwareQuery, useModeAwareMutation } from "@/hooks/useModeAwareQuery";
import { useDocumentApi } from "@/providers/DocumentApiProvider";
import { useAuth } from "@/providers/AuthProvider";
import { useQueryClient } from "@tanstack/react-query";
import type {
  TransactionDocument,
  TransactionDocumentsResponse,
  UploadTransactionDocumentPayload,
} from "@/types/transaction-document";
import {
  DOCUMENT_UPLOAD_TYPE,
} from "@/types/transaction-document";
import type {
  AdmDocumentMetadata,
  AdmFileUploadResponse,
  AdmCreatedDocumentResponse,
  AdmDocumentContext,
} from "@/types/adm-document";
import {
  ADM_LINK_TYPE,
  ADM_LINK_PRODUCT,
  ADM_DOC_TYPE_ID,
  ADM_FOLDER_ID,
  ADM_TAG_IDS,
  ADM_TAG_NAMES,
} from "@/types/adm-document";

// ---------------------------------------------------------------------------
// Mock data — demo mode
// ---------------------------------------------------------------------------

const mockDocuments: TransactionDocument[] = [
  {
    id: "tdoc-1",
    fileName: "CrystalReportViewer1 (2).pdf",
    fileType: "application/pdf",
    fileSize: 245678,
    documentType: "Sales Ledger Transaction",
    uploadedAt: "2026-04-20T10:30:00Z",
    uploadedByEmail: "admin@TMCProductions.com",
    transactionReference: "INV-0042",
  },
  {
    id: "tdoc-2",
    fileName: "Remittance_Advice_Mar26.pdf",
    fileType: "application/pdf",
    fileSize: 98765,
    documentType: "Remittance Advice",
    uploadedAt: "2026-03-15T09:15:00Z",
    uploadedByEmail: "finance@TMCProductions.com",
    transactionReference: "INV-0042",
  },
  {
    id: "tdoc-3",
    fileName: "Invoice_INV-0042_original.pdf",
    fileType: "application/pdf",
    fileSize: 312400,
    documentType: "Invoice",
    uploadedAt: "2026-02-01T14:45:00Z",
    uploadedByEmail: "admin@TMCProductions.com",
    transactionReference: "INV-0042",
  },
];

const mockDocumentsResponse: TransactionDocumentsResponse = {
  documents: mockDocuments,
  totalCount: mockDocuments.length,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripExternalIdPrefix(externalId: string): string {
  return externalId.length > 1 ? externalId.substring(1) : externalId;
}

function mimeTypeFromExtension(ext: string): string {
  const map: Record<string, string> = {
    ".pdf": "application/pdf",
    ".png": "image/png",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".gif": "image/gif",
    ".doc": "application/msword",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".xls": "application/vnd.ms-excel",
    ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".csv": "text/csv",
    ".txt": "text/plain",
  };
  return map[ext.toLowerCase()] ?? "application/octet-stream";
}

function mapAdmDocumentToTransactionDocument(
  doc: AdmDocumentMetadata,
  transactionReference: string,
): TransactionDocument {
  const ext = doc.DocFileExtension ?? "";
  return {
    id: String(doc.Id),
    fileName: `${doc.DocTitle}${ext}`,
    fileType: mimeTypeFromExtension(ext),
    fileSize: 0,
    documentType: doc.DocumentType,
    uploadedAt: doc.DocDatePutin,
    uploadedByEmail: doc.UserName,
    transactionReference,
  };
}

async function getAdmAuthHeaders(
  getAccessToken?: () => Promise<string | null>,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (getAccessToken) {
    const token = await getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return headers;
}

function buildTagValues(
  ctx: AdmDocumentContext,
  payload: UploadTransactionDocumentPayload,
) {
  const tags: { TagId: number; TagValue: string }[] = [
    { TagId: ADM_TAG_IDS.DATABASE_NAME, TagValue: ctx.dbName },
  ];

  if (payload.externalId) {
    tags.push({
      TagId: ADM_TAG_IDS.SALES_LEDGER_PRIMARY,
      TagValue: stripExternalIdPrefix(payload.externalId),
    });
  }
  if (payload.accountCode) {
    tags.push({ TagId: ADM_TAG_IDS.ACCOUNT_CODE, TagValue: payload.accountCode });
  }
  if (payload.customerName) {
    tags.push({ TagId: ADM_TAG_IDS.ACCOUNT_NAME, TagValue: payload.customerName });
  }
  if (payload.headerReference) {
    tags.push({ TagId: ADM_TAG_IDS.HEADER_REF, TagValue: payload.headerReference });
  }
  if (payload.transactionDate) {
    tags.push({ TagId: ADM_TAG_IDS.TRANSACTION_DATE, TagValue: payload.transactionDate });
  }
  if (payload.description) {
    tags.push({ TagId: ADM_TAG_IDS.DESCRIPTION, TagValue: payload.description });
  }
  if (payload.transactionType) {
    tags.push({ TagId: ADM_TAG_IDS.TRANSACTION_TYPE, TagValue: payload.transactionType });
  }

  return tags;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch all documents linked to a transaction via ADM DocumentMetadata endpoint.
 * Uses X-DocumentTags header to filter by ExternalId (Sales Ledger Primary).
 */
export function useTransactionDocuments(
  transactionReference: string | undefined,
  externalId?: string,
) {
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareQuery(
    ["transactionDocuments", transactionReference ?? ""],
    async () => {
      console.log("[ADM] useTransactionDocuments — txRef:", transactionReference, "externalId:", externalId);
      if (!externalId) {
        console.log("[ADM] useTransactionDocuments — no externalId, returning empty");
        return { documents: [], totalCount: 0 } as TransactionDocumentsResponse;
      }

      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error("ADM not initialized — could not discover instance");
      }

      const headers = await getAdmAuthHeaders(getAccessToken);

      const strippedId = stripExternalIdPrefix(externalId);
      console.log("[ADM] useTransactionDocuments — strippedExternalId:", strippedId, "dbName:", ctx.dbName, "instanceUrl:", ctx.instanceUrl);
      const tagFilters = JSON.stringify({
        [ADM_TAG_NAMES.SALES_LEDGER_PRIMARY]: strippedId,
        [ADM_TAG_NAMES.DATABASE_NAME]: ctx.dbName,
      });
      console.log("[ADM] useTransactionDocuments — X-DocumentTags:", tagFilters);
      headers["X-DocumentTags"] = tagFilters;

      const params = new URLSearchParams({
        dbName: ctx.dbName,
        linkType: ADM_LINK_TYPE,
        linkProduct: ADM_LINK_PRODUCT,
      });

      const url = `${ctx.instanceUrl}/DocumentMetadata?${params.toString()}`;
      console.log("[ADM] useTransactionDocuments — fetching:", url);
      const response = await fetch(url, { method: "GET", headers });

      if (!response.ok) {
        console.error("[ADM] useTransactionDocuments — DocumentMetadata failed", response.status, response.statusText);
        throw new Error(`ADM DocumentMetadata failed (HTTP ${response.status})`);
      }

      const admDocs: AdmDocumentMetadata[] = await response.json();
      console.log("[ADM] useTransactionDocuments — received", admDocs.length, "documents");

      const documents = admDocs.map((d) =>
        mapAdmDocumentToTransactionDocument(d, transactionReference ?? ""),
      );

      return { documents, totalCount: documents.length } as TransactionDocumentsResponse;
    },
    mockDocumentsResponse,
    {
      enabled: !!transactionReference,
      staleTime: 60 * 1000,
    },
  );
}

/**
 * Upload a document to ADM: FileUpload then CreateDocument (two-step).
 * Tags are populated from the transaction data passed in the payload.
 */
export function useUploadTransactionDocument() {
  const queryClient = useQueryClient();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareMutation<
    TransactionDocument,
    Error,
    UploadTransactionDocumentPayload
  >(
    async (payload) => {
      console.log("[ADM] useUploadTransactionDocument — file:", payload.file.name, "txRef:", payload.transactionReference, "externalId:", payload.externalId);
      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error("ADM not initialized — could not discover instance");
      }

      // Step 1: Upload the file
      console.log("[ADM] Upload Step 1 — FileUpload to", ctx.instanceUrl);
      const uploadHeaders = await getAdmAuthHeaders(getAccessToken);
      delete uploadHeaders["Content-Type"];

      const formData = new FormData();
      formData.append("file", payload.file);

      const uploadResponse = await fetch(
        `${ctx.instanceUrl}/FileUpload`,
        { method: "POST", headers: uploadHeaders, body: formData },
      );

      if (!uploadResponse.ok) {
        console.error("[ADM] FileUpload failed", uploadResponse.status, uploadResponse.statusText);
        throw new Error(`ADM FileUpload failed (HTTP ${uploadResponse.status})`);
      }

      const uploadJson: AdmFileUploadResponse = await uploadResponse.json();
      const identifiers = uploadJson.Identifiers ?? [];
      if (identifiers.length === 0) {
        throw new Error("ADM FileUpload returned no entries");
      }

      const serverFileKey = identifiers[0].ServerFileKey;
      console.log("[ADM] Upload Step 1 complete — serverFileKey:", serverFileKey);

      // Step 2: Create the document with tags
      console.log("[ADM] Upload Step 2 — CreateDocument");
      const createHeaders = await getAdmAuthHeaders(getAccessToken);

      const tagValues = buildTagValues(ctx, payload);
      console.log("[ADM] Upload Step 2 — tags:", tagValues);
      const fileName = payload.file.name;
      const titleWithoutExt = fileName.includes(".")
        ? fileName.substring(0, fileName.lastIndexOf("."))
        : fileName;

      const createBody = {
        ServerFileKey: serverFileKey,
        Attributes: {
          DestinationFolderId: ADM_FOLDER_ID,
          Title: titleWithoutExt,
        },
        IndexingInfo: {
          DocTypeId: ADM_DOC_TYPE_ID,
          TagValues: tagValues,
        },
      };

      const createResponse = await fetch(
        `${ctx.instanceUrl}/Documents`,
        { method: "POST", headers: createHeaders, body: JSON.stringify(createBody) },
      );

      if (!createResponse.ok) {
        console.error("[ADM] CreateDocument failed", createResponse.status, createResponse.statusText);
        throw new Error(`ADM CreateDocument failed (HTTP ${createResponse.status})`);
      }

      const created: AdmCreatedDocumentResponse = await createResponse.json();
      console.log("[ADM] Upload Step 2 complete — created document ID:", created.Id);

      return {
        id: String(created.Id),
        fileName,
        fileType: payload.file.type,
        fileSize: payload.file.size,
        documentType: DOCUMENT_UPLOAD_TYPE,
        uploadedAt: new Date().toISOString(),
        uploadedByEmail: "",
        transactionReference: payload.transactionReference,
      };
    },
    ({ file, transactionReference }) => ({
      id: `tdoc-demo-${Date.now()}`,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      documentType: DOCUMENT_UPLOAD_TYPE,
      uploadedAt: new Date().toISOString(),
      uploadedByEmail: "demo@example.com",
      transactionReference,
    }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["transactionDocuments", variables.transactionReference],
        });
      },
    },
  );
}

/**
 * Delete a document from ADM by its numeric ID.
 */
export function useDeleteTransactionDocument() {
  const queryClient = useQueryClient();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareMutation<
    void,
    Error,
    { documentId: string; transactionReference: string }
  >(
    async ({ documentId }) => {
      console.log("[ADM] useDeleteTransactionDocument — documentId:", documentId);
      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error("ADM not initialized — could not discover instance");
      }

      const headers = await getAdmAuthHeaders(getAccessToken);
      const response = await fetch(
        `${ctx.instanceUrl}/Documents/${documentId}`,
        { method: "DELETE", headers },
      );

      if (!response.ok) {
        console.error("[ADM] Delete failed", response.status, response.statusText);
        throw new Error(`ADM Delete failed (HTTP ${response.status})`);
      }
      console.log("[ADM] Delete successful — documentId:", documentId);
    },
    () => undefined,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["transactionDocuments", variables.transactionReference],
        });
      },
    },
  );
}

/**
 * Download a document from ADM as a Blob and trigger a browser download.
 */
export function useDownloadTransactionDocument() {
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareMutation<
    void,
    Error,
    { documentId: string; fileName: string }
  >(
    async ({ documentId, fileName }) => {
      console.log("[ADM] useDownloadTransactionDocument — documentId:", documentId, "fileName:", fileName);
      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error("ADM not initialized — could not discover instance");
      }

      const headers = await getAdmAuthHeaders(getAccessToken);
      delete headers["Content-Type"];

      const response = await fetch(
        `${ctx.instanceUrl}/Documents/${documentId}/Download`,
        { method: "GET", headers },
      );

      if (!response.ok) {
        console.error("[ADM] Download failed", response.status, response.statusText);
        throw new Error(`ADM Download failed (HTTP ${response.status})`);
      }

      const blob = await response.blob();
      console.log("[ADM] Download successful — blob size:", blob.size);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    () => undefined,
  );
}

/**
 * Preview a document from ADM — opens in a new browser tab.
 */
export function usePreviewTransactionDocument() {
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareMutation<
    void,
    Error,
    { documentId: string }
  >(
    async ({ documentId }) => {
      console.log("[ADM] usePreviewTransactionDocument — documentId:", documentId);
      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error("ADM not initialized — could not discover instance");
      }

      const headers = await getAdmAuthHeaders(getAccessToken);
      delete headers["Content-Type"];

      const response = await fetch(
        `${ctx.instanceUrl}/Documents/${documentId}/preview`,
        { method: "GET", headers },
      );

      if (!response.ok) {
        console.error("[ADM] Preview failed", response.status, response.statusText);
        throw new Error(`ADM Preview failed (HTTP ${response.status})`);
      }

      const blob = await response.blob();
      console.log("[ADM] Preview successful — blob size:", blob.size);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    },
    () => undefined,
  );
}
